import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { CompanySettings, BrandingSettings, ColorSettings, TypographySettings, QuoteSettings, EmailSettings } from '../types';

export class SettingsService {
  private static readonly SETTINGS_ID = 'default';
  private static readonly COLLECTION = 'companySettings';

  static async getSettings(): Promise<CompanySettings> {
    try {
      const docSnap = await getDoc(
        doc(db, this.COLLECTION, this.SETTINGS_ID)
      );
      if (docSnap.exists()) {
        const data = docSnap.data();
        const settings = {
          ...data,
          updatedAt: new Date(data.updatedAt?.toDate?.() || Date.now()),
        } as CompanySettings;

        // Convert Timestamps back to Date objects in PDFTemplates
        if (settings.quotes?.pdfTemplates) {
          settings.quotes = {
            ...settings.quotes,
            pdfTemplates: settings.quotes.pdfTemplates.map((template: any) => ({
              ...template,
              createdAt: template.createdAt?.toDate?.()
                ? new Date(template.createdAt.toDate())
                : template.createdAt instanceof Date
                ? template.createdAt
                : new Date(),
            })),
          };
        }

        return settings;
      }
      // Return default settings if none exist
      return this.getDefaultSettings();
    } catch (error: any) {
      throw new Error(`Failed to get settings: ${error.message}`);
    }
  }

  static async updateSettings(
    updates: Partial<CompanySettings>
  ): Promise<void> {
    try {
      const settingsRef = doc(db, this.COLLECTION, this.SETTINGS_ID);

      // Check if document exists
      const docSnap = await getDoc(settingsRef);

      // Clean and process updates - only include non-undefined values
      const cleanUpdates: any = {};

      // Process each top-level section
      if (updates.branding !== undefined) {
        cleanUpdates.branding = updates.branding;
      }
      if (updates.colors !== undefined) {
        cleanUpdates.colors = updates.colors;
      }
      if (updates.typography !== undefined) {
        cleanUpdates.typography = updates.typography;
      }
      if (updates.email !== undefined) {
        cleanUpdates.email = updates.email;
      }
      if (updates.quotes !== undefined) {
        // Handle quotes with special care for pdfTemplates
        const quotesUpdate: any = { ...updates.quotes };

        // Convert Date objects to Timestamps in PDFTemplates
        if (quotesUpdate.pdfTemplates) {
          quotesUpdate.pdfTemplates = quotesUpdate.pdfTemplates.map((template: any) => ({
            ...template,
            createdAt: template.createdAt instanceof Date
              ? Timestamp.fromDate(template.createdAt)
              : template.createdAt,
          }));
        }

        cleanUpdates.quotes = quotesUpdate;
      }
      if (updates.roles !== undefined) {
        cleanUpdates.roles = updates.roles;
      }

      if (docSnap.exists()) {
        // Update existing document
        await updateDoc(settingsRef, {
          ...cleanUpdates,
          updatedAt: Timestamp.now(),
        });
      } else {
        // Create new document with defaults
        const defaults = this.getDefaultSettings();
        await setDoc(settingsRef, {
          ...defaults,
          ...cleanUpdates,
          id: this.SETTINGS_ID,
          updatedAt: Timestamp.now(),
        });
      }
    } catch (error: any) {
      throw new Error(`Failed to update settings: ${error.message}`);
    }
  }

  private static getDefaultSettings(): CompanySettings {
    const defaultRoleVisibility = {
      admin: {
        canCreateProjects: true,
        canEditProjects: true,
        canDeleteProjects: true,
        canExportPDF: true,
        canViewMaterials: true,
        canViewProcesses: true,
        canViewCosts: true,
        canViewMarkupDetails: true,
        canViewAllProjects: true,
        canViewOnlyOwnProjects: false,
        canManageUsers: true,
        canManageSettings: true,
      },
      sales: {
        canCreateProjects: true,
        canEditProjects: true,
        canDeleteProjects: false,
        canExportPDF: true,
        canViewMaterials: true,
        canViewProcesses: true,
        canViewCosts: true,
        canViewMarkupDetails: true,
        canViewAllProjects: false,
        canViewOnlyOwnProjects: true,
        canManageUsers: false,
        canManageSettings: false,
      },
      viewer: {
        canCreateProjects: false,
        canEditProjects: false,
        canDeleteProjects: false,
        canExportPDF: true,
        canViewMaterials: true,
        canViewProcesses: true,
        canViewCosts: false,
        canViewMarkupDetails: false,
        canViewAllProjects: false,
        canViewOnlyOwnProjects: true,
        canManageUsers: false,
        canManageSettings: false,
      },
    };

    return {
      id: this.SETTINGS_ID,
      branding: {
        companyName: 'Red Max Hub',
        logoUrl: '',
        logoAlt: 'Company Logo',
        faviconUrl: '',
        tagline: 'Professional Quoting Solutions',
        loginPageTitle: 'Redmax Quoting System',
        loginPageSubtitle: 'Sign In',
        loginSignupText: 'Create Account',
      },
      colors: {
        // Header/Navigation
        headerBackground: '#667eea',
        headerText: '#ffffff',
        headerButton: '#764ba2',

        // Main Content
        mainBackground: '#ffffff',
        mainText: '#1f2937',
        secondaryText: '#6b7280',

        // Cards/Windows
        cardBackground: '#ffffff',
        cardText: '#1f2937',

        // Buttons
        primaryButton: '#667eea',
        primaryButtonText: '#ffffff',
        secondaryButton: '#f3f4f6',
        secondaryButtonText: '#1f2937',

        // UI Elements
        border: '#e5e7eb',
        accent: '#f59e0b',
        iconColor: '#667eea',

        // Status Colors
        success: '#10b981',
        warning: '#f59e0b',
        error: '#ef4444',
      },
      typography: {
        fontFamily: 'Inter, sans-serif',
        headingSize: 24,
        bodySize: 14,
        fontScale: {
          h1: 32,
          h2: 24,
          h3: 20,
          body: 14,
        },
      },
      quotes: {
        pdfHeaderText: 'QUOTE',
        pdfFooterText: 'Thank you for your business!',
        quotePrefix: 'QT-',
        showMarkupDetails: false,
        currencySymbol: '$',
        defaultTerms: 'Net 30',
        notesTemplate: '',
        defaultTaxRate: 0,
        pdfTemplates: [
          {
            id: 'template_internal',
            name: 'Internal View',
            description: 'Full details for internal use',
            visibility: {
              showMaterials: true,
              showProcesses: true,
              showMaterialBreakdown: true,
              showMarkupDetails: true,
              showProcessCosts: true,
              showItemTotals: true,
              showSectionTotals: true,
              showUnitCosts: true,
            },
            isDefault: true,
            createdAt: new Date(),
          },
          {
            id: 'template_client',
            name: 'Client View',
            description: 'For client distribution',
            visibility: {
              showMaterials: true,
              showProcesses: false,
              showMaterialBreakdown: false,
              showMarkupDetails: false,
              showProcessCosts: false,
              showItemTotals: false,
              showSectionTotals: true,
              showUnitCosts: false,
            },
            isDefault: false,
            createdAt: new Date(),
          },
        ],
      },
      email: {
        senderEmail: '',
        senderName: 'Red Max Hub',
        quoteSubjectLine: 'Your Quote from Red Max Hub',
        quoteEmailTemplate: 'Please see attached quote.',
      },
      roles: defaultRoleVisibility,
      updatedAt: new Date(),
    };
  }
}
